import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Constants from "../../constants";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisEmptyList from "../../components/controls/emptylist";
import Pagenated from "../../components/pagenated";
import { api, fmtDate, money, reqTypeTitle, tone } from "./hctc_common";
import "./hctc.css";

// Two lists on one component:
//  - /service-applications            the applicant's own applications (screen svc_apply)
//  - /service-desk/:desk              a staff desk (screen = desk code, e.g. svc_registrar)
export default function ServiceApplications() {
  const { desk } = useParams();
  const navigate = useNavigate();
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [counts, setCounts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(Constants.PAGESIZE);
  const [f, setF] = useState({ text: "", requestType: "", status: "", classification: "", divisionId: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const path = (p, s) => (desk ? `serviceapp/desk/${desk}/${p}/${s}` : `serviceapp/my-list/${p}/${s}`);
  const body = (x) => ({ ...x, divisionId: x.divisionId ? Number(x.divisionId) : null, requestType: x.requestType || null, status: x.status || null, classification: x.classification || null });

  const load = async (x, p, s) => {
    setLoading(true);
    setErr("");
    const d = await api(token, setIsExpired, path(p, s), { method: "POST", body: body(x) });
    setLoading(false);
    if (d.code === 0) {
      setItems(d.items || []);
      setCount(d.totalCount || 0);
      if (d.counts) setCounts(d.counts);
      if (d.statuses) setStatuses(d.statuses);
    } else if (d.code !== -10) setErr(d.status || "Unable to load");
  };

  useEffect(() => {
    (async () => {
      const c = await api(token, setIsExpired, "serviceapp/config");
      if (c.code === 0) setCfg(c);
    })();
    const x = { text: "", requestType: "", status: "", classification: "", divisionId: "" };
    setF(x);
    setPageIndex(0);
    load(x, 0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desk]);

  const on = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const onSearch = (x = f) => {
    setPageIndex(0);
    load(x, 0, pageSize);
  };
  const onPick = (pick) => {
    const p = pick.id - 1;
    setPageIndex(p);
    load(f, p, pageSize);
  };
  const onSize = (k) => {
    setPageSize(Number(k));
    setPageIndex(0);
    load(f, 0, Number(k));
  };

  const title = desk ? `${cfg?.desks?.find((d) => d.code === desk)?.title || "Service"} Desk` : "My Service Applications";
  const open = (it) => navigate(desk ? `/service-desk/app/${it.id}` : it.status === "Draft" || it.status === "Rework Requested" ? `/service-applications/apply/${it.id}` : `/service-applications/${it.id}`);

  return (
    <div className="hctc-page">
      <div className="hctc-toolbar">
        <div className="left">
          <div>
            <h2>{title}</h2>
            <div className="hctc-sub">
              {desk
                ? "Certified True Copy, decree, default fee and case search applications on this desk."
                : "Certified True Copies, Decree Absolute/Nisi, Default Fee and Case Search applications."}
            </div>
          </div>
        </div>
        <div className="right">
          {!desk && cfg?.hasMedia && (
            <JisButton variant="outline" onClick={() => navigate("/my-media-instruments")}>
              My Media Instruments
            </JisButton>
          )}
          {!desk && <JisButton onClick={() => navigate("/service-applications/apply")}>New Application</JisButton>}
        </div>
      </div>

      {desk && counts.length > 0 && (
        <div className="hctc-chips">
          <button
            className={`hctc-chip ${!f.status ? "active" : ""}`}
            onClick={() => {
              const x = { ...f, status: "" };
              setF(x);
              onSearch(x);
            }}
          >
            All ({counts.reduce((a, c) => a + c.count, 0)})
          </button>
          {statuses
            .filter((s) => counts.some((c) => c.status === s))
            .map((s) => (
              <button
                key={s}
                className={`hctc-chip ${f.status === s ? "active" : ""}`}
                onClick={() => {
                  const x = { ...f, status: s };
                  setF(x);
                  onSearch(x);
                }}
              >
                {s} ({counts.find((c) => c.status === s)?.count || 0})
              </button>
            ))}
        </div>
      )}

      <div className="hctc-card">
        <div className="hctc-grid">
          <div className="hctc-field span2">
            <label>Application / tracking / case number, title or applicant</label>
            <input name="text" value={f.text} onChange={on} onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </div>
          <div className="hctc-field">
            <label>Service</label>
            <select name="requestType" value={f.requestType} onChange={on}>
              <option value="">All</option>
              {(cfg?.requestTypes || []).map((r) => (
                <option key={r.code} value={r.code}>
                  {r.title}
                </option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>Classification</label>
            <select name="classification" value={f.classification} onChange={on}>
              <option value="">All</option>
              {["Historical", "JIS", "Unknown", "Non-Case"].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          {desk && (
            <div className="hctc-field">
              <label>Division</label>
              <select name="divisionId" value={f.divisionId} onChange={on}>
                <option value="">All</option>
                {frequentData?.divisions?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!desk && (
            <div className="hctc-field">
              <label>Status</label>
              <select name="status" value={f.status} onChange={on}>
                <option value="">All</option>
                {(cfg?.statuses || []).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="hctc-actions" style={{ marginTop: 10 }}>
          <JisButton onClick={() => onSearch()}>Search</JisButton>
        </div>
      </div>
      {err && <div className="hctc-err">{err}</div>}

      <div className="table">
        <div className="section-cases">
          <span>Applications ({count.toLocaleString()})</span>
        </div>
        <div className="table-wrapper" style={{ overflowX: "auto" }}>
          <table className="case-table hctc-table">
            <thead>
              <tr>
                <th>Application No.</th>
                <th>Service</th>
                <th>Case</th>
                {desk && <th>Applicant</th>}
                <th>Classification</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Submitted</th>
                <th className="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {it.applicationNumber || <span className="hctc-muted">Draft #{it.id}</span>}
                    {it.trackingNumber && <div className="hctc-muted">{it.trackingNumber}</div>}
                  </td>
                  <td>{reqTypeTitle(cfg, it.requestType)}</td>
                  <td className="wrap">
                    {it.caseNumber || it.suitNumber || it.legacyCaseNumber || "-"}
                    {it.caseTitle && <div className="hctc-muted">{it.caseTitle}</div>}
                  </td>
                  {desk && <td>{it.fullName}</td>}
                  <td>
                    <span className={`hctc-pill ${tone(it.classification)}`}>{it.classification || "-"}</span>
                  </td>
                  <td>
                    <span className={`hctc-pill ${tone(it.status)}`}>{it.status}</span>
                    {desk && it.assignedTo && <div className="hctc-muted">{it.assignedTo}</div>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{it.amount ? money(it.amount) : "-"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(it.submittedOn || it.createdOn)}</td>
                  <td>
                    <JisButton variant="plain" onClick={() => open(it)}>
                      {!desk && (it.status === "Draft" || it.status === "Rework Requested") ? "Continue" : "Open"}
                    </JisButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && items.length === 0 && <JisEmptyList title={desk ? "No applications on this desk" : "You have no applications yet"} />}
        </div>
        {count > 0 && <Pagenated onChangePageSize={onSize} size={pageSize} total={count} maxPage={20} onPick={onPick} />}
      </div>
      <BackLoader open={loading} />
    </div>
  );
}
